// One writer per persistent machine, across every tab of this browser (Web Locks). A real
// browser-held lease, not a heartbeat guess: closing a document releases it even if its cleanup
// never runs. Ported from EndisOS src/linux/machine-lock.ts.
import { LinuxError } from "./engine.js";

export const MACHINE_ID_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;
export const lockName = (id) => "revault:linux:machine:" + id;

// Resolves to release() once held; rejects LOCKED when another tab holds it.
export function acquireMachineLock(id, signal, locks = typeof navigator !== "undefined" ? navigator.locks : undefined) {
	if (!MACHINE_ID_RE.test(id)) return Promise.reject(new LinuxError("PROCESS_FAILED", "Invalid machine id."));
	if (!locks) return Promise.reject(new LinuxError("LOCKED", "This browser can't protect Linux disks across tabs (Web Locks unavailable). Use a current Chrome, Edge, Firefox or Safari."));
	if (signal && signal.aborted) return Promise.reject(new LinuxError("ABORTED", "Machine start cancelled."));
	return new Promise((resolve, reject) => {
		let release = null;
		const onAbort = () => { if (release) release(); reject(new LinuxError("ABORTED", "Machine start cancelled.")); };
		if (signal) signal.addEventListener("abort", onAbort, { once: true });
		locks.request(lockName(id), { mode: "exclusive", ifAvailable: true }, async (lock) => {
			if (!lock) throw new LinuxError("LOCKED", "This machine is already running in another Re:Vault tab. Stop it there first.");
			if (signal && signal.aborted) throw new LinuxError("ABORTED", "Machine start cancelled.");
			// From here the engine's teardown owns the release: an aborted start must finish
			// destroying its runtime before a second writer can take the machine.
			if (signal) signal.removeEventListener("abort", onAbort);
			await new Promise((done) => { release = done; resolve(done); });
		}).catch(reject).finally(() => { if (signal) signal.removeEventListener("abort", onAbort); });
	});
}

// Serialises a read-modify-write of shared Linux records across tabs; runs `fn` directly when
// Web Locks are missing (single-tab browsers can't race).
export function withMutationLock(name, fn, locks = typeof navigator !== "undefined" ? navigator.locks : undefined) {
	if (!locks) return Promise.resolve().then(fn);
	return locks.request("revault:linux:mutate:" + name, { mode: "exclusive" }, fn);
}
